import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import type { ApiResponse } from "./api-client.service";

import type {
  Module,
  ModulesFilter,
  ModulesResponse,
  ModulePayload,
} from "../types/rbac-modules.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.rbacModules;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const rbacModulesService = {
  async list(filter: ModulesFilter): Promise<ModulesResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<ModulesResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load module list.") as unknown as ModulesResponse;
    }
  },

  async details(id: number | string): Promise<ApiResponse<Module>> {
    try {
      return await apiClient<ApiResponse<Module>>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load module details.") as unknown as ApiResponse<Module>;
    }
  },

  async create(payload: ModulePayload): Promise<ApiResponse<Module>> {
    try {
      return await apiClient<ApiResponse<Module>>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to create module.") as unknown as ApiResponse<Module>;
    }
  },

  async update(id: number | string, payload: Partial<ModulePayload>): Promise<ApiResponse<Module>> {
    try {
      return await apiClient<ApiResponse<Module>>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to update module.") as unknown as ApiResponse<Module>;
    }
  },

  async delete(id: number | string): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to delete module.") as unknown as ApiResponse<unknown>;
    }
  },
};
